import { Space, Radio, Select, Typography, Alert, Spin } from 'antd';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { jt, t } from 'ttag';

import Emphasis from 'common/components/Emphasis';
import NotFoundContent from 'common/components/NotFoundContent';
import { SALES_EMAIL } from 'common/services/support/types';

import { LICENSE_TYPE_EVENT_CREDIT, LICENSE_TYPE_USER_CREDIT } from '@domains/Organization/service';

import { useDuplicateEventSearch } from './queries';

const SOURCE_BLANK = 'blank';
export const SOURCE_DUPLICATE = 'duplicate';

const Content = ({
  currentMode,
  hasContract,
  source,
  sourceEvent,
  setSourceEvent,
  setSource,
  orgId,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: events = [], isFetching } = useDuplicateEventSearch(orgId, search, open);

  const debouncedSetSearch = useMemo(() => debounce(setSearch, 250), []);

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  // While open: a fetch in flight or the debounce gap before the keystroke commits to `search`.
  // The gap term keeps the spinner up so the empty state never flashes mid-search.
  const isSearching = open && (isFetching || search === '');

  const handleEventSearch = value => {
    setOpen(value !== '');
    debouncedSetSearch(value);
  };

  const handleEventSelect = option => {
    setSourceEvent(events.find(event => event.id === option.value));
    setOpen(false);
  };

  const handleSourceChange = e => {
    const src = e.target?.value;
    setSource(src);
    if (src === SOURCE_BLANK && sourceEvent?.id) {
      setSourceEvent(null);
    }
  };

  if (currentMode === LICENSE_TYPE_USER_CREDIT && !hasContract) {
    const contactUs = (
      <a key="contact-us" href={`mailto:${SALES_EMAIL}`} target="_blank" rel="noreferrer noopener">
        {t`contact us`}
      </a>
    );
    return (
      <Space orientation="vertical" style={{ width: '100%' }}>
        <Typography.Title level={5}>{t`Content`}</Typography.Title>
        <Alert
          showIcon
          type="error"
          title={t`No Active Contract`}
          description={jt`Your organization is under User Credit License, but there is no active contract. Please ${contactUs} to resolve this.`}
        />
      </Space>
    );
  }

  return (
    <Space orientation="vertical" style={{ width: '100%' }}>
      <Radio.Group onChange={handleSourceChange} value={source}>
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Radio value={SOURCE_BLANK}>
            {t`Blank`}
            <br />
            <Emphasis>{t`Start with a new empty event`}</Emphasis>
          </Radio>
          <Radio value={SOURCE_DUPLICATE}>
            {t`Duplicate Previous Event`}
            <br />
            <Emphasis>{t`Start with a previous event design and library set up`}</Emphasis>
          </Radio>
        </Space>
      </Radio.Group>
      {source === SOURCE_DUPLICATE && (
        <Select
          style={{ width: 400, marginLeft: 25 }}
          value={
            sourceEvent && {
              value: sourceEvent.id,
              label: `${sourceEvent.name} (${sourceEvent.code})`,
            }
          }
          showSearch={{ onSearch: handleEventSearch, filterOption: false }}
          open={open}
          loading={isSearching}
          placeholder={t`Search for an event to duplicate`}
          options={events.map(event => ({
            value: event.id,
            label: `${event.name} (${event.code})`,
          }))}
          labelInValue
          onSelect={handleEventSelect}
          notFoundContent={
            isSearching ? (
              <div style={{ textAlign: 'center', margin: '1em' }}>
                <Spin size="small" />
              </div>
            ) : (
              <NotFoundContent />
            )
          }
          onOpenChange={visible => {
            if (!visible) setOpen(false);
          }}
        />
      )}
    </Space>
  );
};

Content.propTypes = {
  currentMode: PropTypes.oneOf([LICENSE_TYPE_EVENT_CREDIT, LICENSE_TYPE_USER_CREDIT]).isRequired,
  hasContract: PropTypes.bool.isRequired,
  source: PropTypes.string,
  sourceEvent: PropTypes.shape({}),
  setSourceEvent: PropTypes.func,
  setSource: PropTypes.func,
  orgId: PropTypes.string.isRequired,
};

export default Content;
